import React, { useState } from "react";
import "../../Styles/EmployeeForm.css";
import ApiService from "../../nhrc_apis/ApiService";

const { OtpAPI, UploadAPI } = ApiService;

function Employeer({
  personalData,
  onBackToPersonal,
  onNextForm,
  memberId,
  membershipId,
}) {
  const [formData, setFormData] = useState({
    organizationName: "",
    industry: "",
    designation: "",
    department: "",
    companyUrl: "",
    workingLocation: "",
    companyStrength: "",
    employeeId: "",
    experience: "",
    officialEmail: "",
    referralId: "",
  });

  const [enteredOtp, setEnteredOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "officialEmail") {
      setOtpSent(false);
      setOtpVerified(false);
      setEnteredOtp(["", "", "", "", "", ""]);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 2) {
      alert("Please upload maximum 2 files (Front & Back)");
      return;
    }
    setSelectedFiles(files);
    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (!formData.organizationName.trim()) newErrors.organizationName = "Organization name is required";
    if (!formData.industry) newErrors.industry = "Please select industry";
    if (!formData.designation) newErrors.designation = "Please select designation";
    if (!formData.department) newErrors.department = "Please select department";

    if (!formData.companyUrl.trim()) {
      newErrors.companyUrl = "Company website is required";
    } else if (!websiteRegex.test(formData.companyUrl)) {
      newErrors.companyUrl = "Enter valid website URL";
    }

    if (!formData.workingLocation.trim()) newErrors.workingLocation = "Working location is required";
    if (!formData.companyStrength) newErrors.companyStrength = "Please select company strength";
    if (!formData.employeeId.trim()) newErrors.employeeId = "Employee ID is required";
    if (!formData.experience) newErrors.experience = "Experience is required";

    if (!formData.officialEmail.trim()) {
      newErrors.officialEmail = "Official email is required";
    } else if (!emailRegex.test(formData.officialEmail)) {
      newErrors.officialEmail = "Enter valid email";
    }

    if (selectedFiles.length === 0) newErrors.files = "Please upload ID proof";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!formData.officialEmail.trim()) {
      alert("Please enter official email");
      return;
    }

    try {
      setSendingOtp(true);
      const cleanEmail = formData.officialEmail.trim().toLowerCase();
      console.log("SENDING OTP EMAIL:", cleanEmail);

      // ✅ FIXED — now sends purpose + candidate_type, matching what the live backend requires
      await OtpAPI.sendOtp(cleanEmail, "REGISTER", "employee");

      setOtpSent(true);
      setOtpVerified(false);
      setEnteredOtp(["", "", "", "", "", ""]);
      alert("OTP sent successfully");
    } catch (err) {
      console.error("OTP ERROR:", err.response?.data || err);
      alert(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;

    const updatedOtp = [...enteredOtp];
    updatedOtp[index] = value;
    setEnteredOtp(updatedOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !enteredOtp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = enteredOtp.join("").trim();

    if (otpValue.length !== 6) {
      alert("Enter valid 6-digit OTP");
      return;
    }

    try {
      const cleanEmail = formData.officialEmail.trim().toLowerCase();
      console.log("VERIFY EMAIL:", cleanEmail);
      console.log("VERIFY OTP:", otpValue);

      await OtpAPI.verifyOtp({
        email: cleanEmail,
        otp: otpValue,
      });

      setOtpVerified(true);
      alert("Email verified successfully");
    } catch (err) {
      console.error("OTP VERIFY ERROR:", err.response?.data || err);
      setOtpVerified(false);
      alert(err?.response?.data?.detail || "Invalid OTP");
    }
  };

  const uploadFile = async (file) => {
    if (!file) return "";

    const fd = new FormData();
    fd.append("file", file);
    fd.append("membership_id", membershipId);
    fd.append("file_type", "employee_id");

    try {
      const res = await UploadAPI.uploadFile(fd);
      console.log("UPLOAD RESPONSE:", res.data);
      return (
        res?.data?.file_url ||
        res?.data?.fileUrl ||
        res?.data?.url ||
        res?.data?.file_path ||
        ""
      );
    } catch (err) {
      console.error("UPLOAD ERROR:", err.response?.data || err);
      return "";
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();

    if (loading) return;

    const isValid = validateForm();
    if (!isValid) return;

    if (!otpVerified) {
      alert("Please verify official email before proceeding");
      return;
    }

    try {
      setLoading(true);

      let frontUrl = "";
      let backUrl = "";

      if (selectedFiles[0]) {
        frontUrl = await uploadFile(selectedFiles[0]);
      }

      if (selectedFiles[1]) {
        backUrl = await uploadFile(selectedFiles[1]);
      }

      const payload = {
        member_id: Number(memberId),
        membership_id: String(membershipId),
        organization_name: formData.organizationName.trim(),
        industry: formData.industry,
        designation: formData.designation,
        department: formData.department,
        company_website: formData.companyUrl.trim(),
        working_location: formData.workingLocation.trim(),
        company_strength: formData.companyStrength,
        employee_id: String(formData.employeeId),
        experience: Number(formData.experience || 0),
        official_email: formData.officialEmail.trim().toLowerCase(),
        user_email: formData.officialEmail.trim().toLowerCase(),
        email_otp: enteredOtp.join(""),
        referral_id: formData.referralId || "",
        id_card_front: frontUrl || "",
        id_card_back: backUrl || "",
        password: "",
        confirm_password: "",
      };

      console.log("FINAL EMPLOYEE PAYLOAD:", payload);

      if (onNextForm) {
        onNextForm(payload);
      }
    } catch (err) {
      console.error("SUBMIT ERROR:", err.response?.data || err);
      alert(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Submission failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="personaldetails-section">
      <h3 className="heading">EMPLOYEE DETAILS</h3>

      <form className="employee-form" onSubmit={handleNext}>
        <div className="row">
          <div className="col-md-6">
            <label>Organization Name*</label>
            <input
              type="text"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              required
            />
            {errors.organizationName && (
              <small className="text-danger">{errors.organizationName}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Industry*</label>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
            >
              <option value="">Select Industry</option>
              <option value="IT">IT</option>
              <option value="Education">Education</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Finance">Finance</option>
              <option value="Others">Others</option>
            </select>
            {errors.industry && (
              <small className="text-danger">{errors.industry}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Designation*</label>
            <select
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
            >
              <option value="">Select</option>
              <option value="Developer">Developer</option>
              <option value="Manager">Manager</option>
              <option value="HR">HR</option>
            </select>
            {errors.designation && (
              <small className="text-danger">{errors.designation}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Department*</label>
            <select
              name="department"
              value={formData.department}
              onChange={handleChange}
              required
            >
              <option value="">Select</option>
              <option value="Development">Development</option>
              <option value="HR">HR</option>
              <option value="Finance">Finance</option>
            </select>
            {errors.department && (
              <small className="text-danger">{errors.department}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Company Website*</label>
            <input
              type="text"
              name="companyUrl"
              value={formData.companyUrl}
              onChange={handleChange}
              required
            />
            {errors.companyUrl && (
              <small className="text-danger">{errors.companyUrl}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Working Location*</label>
            <input
              type="text"
              name="workingLocation"
              value={formData.workingLocation}
              onChange={handleChange}
              required
            />
            {errors.workingLocation && (
              <small className="text-danger">{errors.workingLocation}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Company Strength*</label>
            <select
              name="companyStrength"
              value={formData.companyStrength}
              onChange={handleChange}
              required
            >
              <option value="">Select</option>
              <option value="1-50">1-50</option>
              <option value="50-100">50-100</option>
              <option value="100+">100+</option>
            </select>
            {errors.companyStrength && (
              <small className="text-danger">{errors.companyStrength}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Employee ID*</label>
            <input
              type="text"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              required
            />
            {errors.employeeId && (
              <small className="text-danger">{errors.employeeId}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Experience*</label>
            <input
              type="number"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              required
            />
            {errors.experience && (
              <small className="text-danger">{errors.experience}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>ID Proof*</label>
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              required
            />
            {errors.files && (
              <small className="text-danger">{errors.files}</small>
            )}
          </div>

          <div className="col-md-6">
            <label>Official Email*</label>
            <div className="email-row">
              <input
                type="email"
                name="officialEmail"
                value={formData.officialEmail}
                onChange={handleChange}
                placeholder="Enter your official company email"
              />
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={sendingOtp}
              >
                {sendingOtp ? "Sending..." : "Send OTP"}
              </button>
            </div>
            {errors.officialEmail && (
              <small className="text-danger">{errors.officialEmail}</small>
            )}
          </div>

          {otpSent && !otpVerified && (
            <div className="col-12 otp-section">
              <label>Enter OTP*</label>
              <div className="otp-box-container">
                {enteredOtp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className="otp-box"
                  />
                ))}
              </div>
              <button
                type="button"
                className="verify-btn"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </button>
            </div>
          )}

          {otpVerified && (
            <div className="col-12">
              <small style={{ color: "green", fontWeight: "600" }}>
                Email Verified Successfully
              </small>
            </div>
          )}

          <div className="col-md-6">
            <label>Referral ID</label>
            <input
              type="text"
              name="referralId"
              value={formData.referralId}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onBackToPersonal}>
            Back
          </button>
          <button type="submit" disabled={loading}>
            {loading ? "Processing..." : "Next"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Employeer;